import { useFormContext } from 'react-hook-form'
import { useDebouncedCallback } from '@/hooks'
import { AdvancedFilter, FilterField } from '@/components/atoms/AdvancedFilter'
import { TextInput } from '@/components/atoms'
import { ORDER_STATUS_OPTIONS } from '@/constants/statuses'
import { useAuth } from '@/contexts/AuthContext'
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext'
import { usePosLookups } from '@/components/features/pos/usePosLookups'

/** `fixedChannel`: the list's channel is set by the page (Point of sale → Orders); only its branch filter shows. */
export const OrdersFilters = ({ fixedChannel }: { fixedChannel?: string } = {}) => {
    const { setValue, control, watch } = useFormContext()
    const currentFilters = watch('filters')
    const { can } = useAuth()
    // Till orders are a separate list (`pos:list`); only businesses with the POS see the channel filter (C33).
    const posAllowed = useFeatureFlags().isFeatureEnabled('pos') && can('pos:list')
    const showChannel = posAllowed && !fixedChannel
    const { branches, terminals } = usePosLookups(posAllowed)
    // A branch is filtered by its tills (orders carry `terminal_id`, not a branch).
    const branchOptions = branches
        .map((b) => ({ label: b.name, value: terminals.filter((t) => t.branch_id === b._id).map((t) => t._id).join(',') }))
        .filter((o) => o.value)

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        debouncedSearch(e.target.value)
    }

    const debouncedSearch = useDebouncedCallback((value: string) => {
        setValue('filters.search', value)
        setValue('pagination.currentPage', 1)
    }, 400)

    const filterFields: FilterField[] = [
        {
            name: 'filters.status',
            label: 'Order status',
            type: 'select',
            placeholder: 'Filter by status',
            options: [
                { label: 'All statuses', value: 'all' },
                ...ORDER_STATUS_OPTIONS,
            ],
        },
        {
            name: 'filters.payment_status',
            label: 'Payment status',
            type: 'select',
            placeholder: 'Filter by payment',
            options: [
                { label: 'All payments', value: 'all' },
                { label: 'Paid', value: 'paid' },
                { label: 'Unpaid', value: 'unpaid' },
                { label: 'Failed', value: 'failed' },
                { label: 'Refunded', value: 'refunded' },
            ],
        },
        {
            name: 'filters.payment_method',
            label: 'Payment method',
            type: 'select',
            placeholder: 'Filter by method',
            options: [
                { label: 'All methods', value: 'all' },
                { label: 'COD', value: 'cod' },
                { label: 'SSLCommerz', value: 'ssl_commerz' },
                { label: 'bKash', value: 'bkash' },
                { label: 'Nagad', value: 'nagad' },
            ],
        },
        ...(posAllowed
            ? [
                  ...(showChannel
                      ? [
                            {
                                name: 'filters.channel',
                                label: 'Channel',
                                type: 'select' as const,
                                placeholder: 'Online store',
                                options: [
                                    { label: 'Online store', value: 'online' },
                                    { label: 'Point of sale', value: 'pos' },
                                ],
                            },
                        ]
                      : []),
                  ...(currentFilters?.channel === 'pos'
                      ? [
                            {
                                name: 'filters.terminal_id',
                                label: 'Branch',
                                type: 'select' as const,
                                placeholder: 'All branches',
                                options: [{ label: 'All branches', value: 'all' }, ...branchOptions],
                            },
                        ]
                      : []),
              ]
            : []),
        {
            name: 'filters.dateRange',
            label: 'Date range',
            type: 'dateRange',
        },
    ]

    // Count active filters excluding search
    const activeFiltersCount = Object.entries(currentFilters || {}).filter(([key, value]) => {
        if (key === 'search') return false
        if (key === 'channel' && fixedChannel) return false
        if (value === undefined || value === null || value === 'all') return false
        return true
    }).length

    return (
        <div className="flex w-full flex-col items-stretch gap-2 sm:flex-row sm:items-center">
            <div className="w-full min-w-0 sm:w-64">
                <TextInput
                    control={control}
                    name="filtersForm.search"
                    label=""
                    placeholder="Search by ref, name or phone"
                    onChange={handleSearchChange}
                    className="w-full space-y-0"
                />
            </div>

            <div className="flex items-center gap-2">
                <AdvancedFilter
                    fields={filterFields}
                    activeFiltersCount={activeFiltersCount}
                    onClear={() => setValue('pagination.currentPage', 1)}
                />
            </div>
        </div>
    )
}
